import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { translateString } from '@src/util/intl/translateString';

import { STATUS_PER_DEFAULT } from './constants';

export const ACTIVE_USER_FILTER_FIELDS = {
  TEXT: 'text',
  STATUS: 'status',
  STALLED: 'stalled',
  LOW_BATTERY: 'lowBattery',
} as const;

export const INIT_FILTERS: FilterValues = {
  text: {
    value: '',
    label: translateString('dashboards.active_user.filters'),
    valueType: 'string',
  },
  status: {
    value: STATUS_PER_DEFAULT,
    label: translateString('dashboards.active_user.filter.show'),
    valueTranslated: translateString('dashboards.active_user.filters.show.connected'),
    valueType: 'boolean',
    clearValue: null,
  },
  stalled: {
    value: false,
    label: translateString('activeUserDashboard.filter.stalled'),
    valueType: 'boolean',
    clearValue: false,
    hideWhenCleared: true,
  },
  lowBattery: {
    value: false,
    label: translateString('activeUserDashboard.filter.lowBattery'),
    valueType: 'boolean',
    clearValue: false,
    hideWhenCleared: true,
  },
};

export const getTextFromFilters = (filters: FilterValues): string =>
  String(filters[ACTIVE_USER_FILTER_FIELDS.TEXT]?.value ?? '');

export const getStatusFromFilters = (filters: FilterValues): boolean | null => {
  const raw = filters[ACTIVE_USER_FILTER_FIELDS.STATUS]?.value;

  if (raw === null) {
    return null;
  }

  if (raw === true || raw === false) {
    return raw;
  }

  return STATUS_PER_DEFAULT;
};

export const getStalledFromFilters = (filters: FilterValues): boolean =>
  filters[ACTIVE_USER_FILTER_FIELDS.STALLED]?.value === true;

export const getLowBatteryFromFilters = (filters: FilterValues): boolean =>
  filters[ACTIVE_USER_FILTER_FIELDS.LOW_BATTERY]?.value === true;

export const buildActiveUserTextFilterSetValueProps = (
  value: string,
  intl: IntlShape,
): SetValueProps => ({
  key: ACTIVE_USER_FILTER_FIELDS.TEXT,
  value,
  label: intl.formatMessage({ id: 'dashboards.active_user.filters' }),
  valueType: 'string',
});

export const buildActiveUserStatusFilterSetValueProps = (
  value: boolean | null,
  intl: IntlShape,
): SetValueProps => {
  let valueTranslated: string;

  if (value === true) {
    valueTranslated = intl.formatMessage({ id: 'dashboards.active_user.filters.show.connected' });
  } else if (value === false) {
    valueTranslated = intl.formatMessage({
      id: 'dashboards.active_user.filters.show.no.connected',
    });
  } else {
    valueTranslated = intl.formatMessage({ id: 'general.all' });
  }

  return {
    key: ACTIVE_USER_FILTER_FIELDS.STATUS,
    value,
    label: intl.formatMessage({ id: 'dashboards.active_user.filter.show' }),
    valueTranslated,
    valueType: value === null ? 'null' : 'boolean',
    clearValue: null,
  };
};

export const buildActiveUserStalledFilterSetValueProps = (
  value: boolean,
  intl: IntlShape,
): SetValueProps => ({
  key: ACTIVE_USER_FILTER_FIELDS.STALLED,
  value,
  label: intl.formatMessage({ id: 'activeUserDashboard.filter.stalled' }),
  valueTranslated: value ? intl.formatMessage({ id: 'general.yes' }) : '',
  valueType: 'boolean',
  clearValue: false,
  hideWhenCleared: true,
});

export const buildActiveUserLowBatteryFilterSetValueProps = (
  value: boolean,
  intl: IntlShape,
): SetValueProps => ({
  key: ACTIVE_USER_FILTER_FIELDS.LOW_BATTERY,
  value,
  label: intl.formatMessage({ id: 'activeUserDashboard.filter.lowBattery' }),
  valueTranslated: value ? intl.formatMessage({ id: 'general.yes' }) : '',
  valueType: 'boolean',
  clearValue: false,
  hideWhenCleared: true,
});
